import type { Procedure } from '@medplum/fhirtypes';

import { Grid, Stack, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import type { FhirView } from '../../core/view-contract';
import { resourceDisplayName } from '../accessors';
import { formatFhirDate } from '../observation/format';

/** The exported Procedure component. Surgical-history lists are the primary surface. */
export const ProcedureView: FhirView<Procedure> = (props) => {
  if (props.ctx.inList) return <ProcedureRowView {...props} />;
  return <ProcedureReadView {...props} />;
};

function performedText(data: Procedure): string | undefined {
  return formatFhirDate(data.performedDateTime ?? data.performedPeriod?.start);
}

/** Procedure-list row: name, status (when not completed), performed date. */
const ProcedureRowView: FhirView<Procedure> = ({ data }) => {
  const date = performedText(data);
  return (
    <Grid
      data-view="procedure-row"
      align="center"
      className="grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-2"
    >
      <Stack direction="horizontal" gap={2} align="center" className="min-w-0">
        <Text inline size="sm" weight="regular" className="truncate">
          {resourceDisplayName(data)}
        </Text>
        {data.status !== 'completed' && <FhirStatusTag status={data.status} />}
      </Stack>
      {date && (
        <Text inline size="xs" color="tertiary">
          {date}
        </Text>
      )}
    </Grid>
  );
};

/** Standalone card. */
const ProcedureReadView: FhirView<Procedure> = ({ data, ctx }) => {
  const date = performedText(data);
  const performer = data.performer?.[0]?.actor?.display;
  const reason = data.reasonCode?.[0]?.text;
  return (
    <div
      data-view="procedure-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold" className="truncate">
          {resourceDisplayName(data)}
        </Text>
        <FhirStatusTag status={data.status} />
      </Stack>
      <Stack
        direction="horizontal"
        wrap
        align="stretch"
        className="mt-1 gap-x-4"
      >
        {date && (
          <Text inline size="xs" color="tertiary">
            {date}
          </Text>
        )}
        {performer && (
          <Text inline size="xs" color="tertiary">
            {performer}
          </Text>
        )}
        {reason && (
          <Text inline size="xs" color="tertiary">
            {reason}
          </Text>
        )}
      </Stack>
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
